import React, { useEffect, useState } from "react";
import moment from "moment";

// Interfaces
import { Message } from "../../interfaces/Message";
import { Participant } from "../../interfaces/Participant";
import { AdminUser } from "../../interfaces/AdminUser";

// Firebase
import { firestore, functions } from "../../firebase/firebase";
import { addDoc, collection, getDocs, onSnapshot, orderBy, query, doc, updateDoc, getDoc, limit } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { genDoc, addParticipantLog } from "../../utils/firebase";

// Mui
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import InputAdornment from "@mui/material/InputAdornment";

// Hooks
import { useFetchUser } from "../../hooks/useFetchUser";

// Icons
import { MdSend } from "react-icons/md";
import { newPrivateChatNotify } from "../../utils/email";

interface Props {
    participantId: string;
    adminId?: string;
    isFullscreen?: boolean;
    dashboardChat?: boolean;
}

const ParticipantChat: React.FC<Props> = ({ participantId, adminId, dashboardChat }) => {
    const { currentAdmin, currentParticipant } = useFetchUser();

    // States
    const [participants, setParticipants] = useState<Participant[]>([]);
    const [admins, setAdmins] = useState<AdminUser[]>([]);
    const [messages, setMessages] = useState<Message[]>([]);
    const [message, setMessage] = useState("");
    const [chat, setChat] = useState(false);

    const fetchParticipants = async () => {
        // Only fetch the one participant involved in this chat
        const snapshot = await getDoc(doc(firestore, "Participants", participantId));
        const data = snapshot.data() as Participant;
        if (data) setParticipants([data]);
    };

    const fetchAdmins = async () => {
        const payload = await getDocs(collection(firestore, "Users"));
        const data = payload.docs.map(d => d.data()) as AdminUser[];
        setAdmins(data);
    };

    const fetchMessages = () => {
        const recentMessagesQuery = query(collection(firestore, "Participants", participantId, "Messages"), orderBy("createdAt", "desc"), limit(100));

        return onSnapshot(recentMessagesQuery, snapshot => {
            const data = snapshot.docs.map(d => d.data()) as Message[];
            data.sort((a, b) => {
                if (a.createdAt && b.createdAt) {
                    return (a.createdAt as any).seconds > (b.createdAt as any).seconds ? 1 : -1;
                }
                return 1;
            });

            setMessages(data);
        });
    };

    const handleChatContainer = () => {
        if (dashboardChat) setChat(!chat);
    };

    const handleSendMessage = async () => {
        const localMessage: Message = {
            createdAt: new Date(),
            updatedAt: new Date(),
            isDeleted: false,
            text: message,
        };

        if (adminId) localMessage.adminId = adminId;
        if (participantId) localMessage.participantId = participantId;

        const createPayload = await addDoc(collection(firestore, "Participants", participantId, "Messages"), localMessage);
        await updateDoc(doc(firestore, "Participants", participantId, "Messages", createPayload.id), { id: createPayload.id });

        const participantPayload = await getDoc(doc(firestore, "Participants", participantId));
        const participant = genDoc<Participant>()(participantPayload);

        if (participant && participant.id) {
            await addParticipantLog(participant.id, "Sent message in personal chat");
        }

        // Notify the admin
        const sendEmail = httpsCallable(functions, "sendEmail");

        let coordinatorEmail = "";

        if (participant.coordinatorId) {
            const payload = await getDoc(doc(firestore, "Users", participant.coordinatorId));
            const coordinator = genDoc<AdminUser>()(payload);

            coordinatorEmail = coordinator.email;
        }

        if (participant)
            await sendEmail({
                to: coordinatorEmail ?? "syn2-coordinators@leadhouse.ca",
                from: participant.email,
                subject: `You have a new private message - Synergic-2 Portal Notification`,
                text: `Synergic-2 Portal Notification`,
                html: newPrivateChatNotify(participant.firstName, participant.email, message),
            });

        setMessage("");
    };

    const isSameUser = (u: AdminUser | Participant | undefined) => {
        let sameUser = false;

        if (u) {
            if (u.id === adminId || (!adminId && u.id === participantId)) sameUser = true;
        }

        return sameUser;
    };

    const getUser = (m: Message) => {
        if (m.adminId) {
            const admin = admins.find(a => a.id === m.adminId);
            return admin;
        } else {
            const part = participants.find(p => p.id === m.participantId);
            return part;
        }
    };

    const scrollToBottom = () => {
        const element = document.getElementById((messages.length - 1).toString());

        if (element) element.scrollIntoView({ block: "center" });
    };

    useEffect(() => {
        if (messages.length > 0) scrollToBottom();
    }, [messages]);

    useEffect(() => {
        fetchParticipants();
        fetchAdmins();
        handleChatContainer();
        const unsub = fetchMessages();
        return () => unsub();
    }, []); // eslint-disable-line

    return (
        <div className={dashboardChat ? "chatDashboard__container" : "chat__container"}>
            <div className="chat__output">
                {messages && messages.length ? (
                    messages.map((m, i) => {
                        return (
                            <div
                                key={i}
                                id={i.toString()}
                                style={
                                    dashboardChat
                                        ? {
                                              display: "flex",
                                              justifyContent:
                                                  (currentAdmin && currentAdmin.id === m.adminId) ||
                                                  (currentParticipant && currentParticipant.id === m.participantId)
                                                      ? "flex-start"
                                                      : "flex-end",
                                          }
                                        : {
                                              display: "flex",
                                              justifyContent:
                                                  (currentAdmin && currentAdmin.id === m.adminId) ||
                                                  (currentParticipant && currentParticipant.id === m.participantId)
                                                      ? "flex-start"
                                                      : "flex-end",
                                          }
                                }
                            >
                                <div className="chat__messages">
                                    <Tooltip title={`Sent by ${getUser(m)?.email}`}>
                                        <h5
                                            className="chat__messages--user"
                                            style={
                                                (currentAdmin && currentAdmin.id === m.adminId) ||
                                                (currentParticipant && currentParticipant.id === m.participantId)
                                                    ? { paddingRight: 10 }
                                                    : { marginLeft: 10, textAlign: "end" }
                                            }
                                        >
                                            {typeof getUser(m) !== "string" && isSameUser(getUser(m)) ? "You" : getUser(m)?.firstName}
                                        </h5>
                                    </Tooltip>
                                    <Tooltip title={`Sent on ${moment((m.createdAt as any).seconds * 1000).format("DD/MM/YYYY hh:mm:ss")}`}>
                                        <h6
                                            className="chat__messages--content"
                                            style={
                                                dashboardChat
                                                    ? (currentAdmin && currentAdmin.id === m.adminId) ||
                                                      (currentParticipant && currentParticipant.id === m.participantId)
                                                        ? {
                                                              marginLeft: 20,
                                                          }
                                                        : {
                                                              display: "flex",
                                                              placeContent: "flex-end",
                                                              alignSelf: "end",
                                                              marginRight: 20,
                                                              width: "min-content",
                                                          }
                                                    : (currentAdmin && currentAdmin.id === m.adminId) ||
                                                      (currentParticipant && currentParticipant.id === m.participantId)
                                                    ? {
                                                          backgroundColor: "#063336",
                                                          marginLeft: 20,
                                                      }
                                                    : {
                                                          display: "flex",
                                                          placeContent: "flex-end",
                                                          backgroundColor: "black",
                                                          alignSelf: "end",
                                                          marginRight: 20,
                                                      }
                                            }
                                        >
                                            {m.text}
                                        </h6>
                                    </Tooltip>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="centered__parent">
                        <h4>No messages</h4>
                    </div>
                )}
            </div>

            <TextField
                fullWidth
                variant="outlined"
                label="Message"
                placeholder="Type your message and then press enter"
                value={message}
                onChange={e => setMessage(e.target.value)}
                onKeyDown={ev => {
                    if (ev.key === "Enter") {
                        handleSendMessage();
                    }
                }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                className="chat__submit"
                                onClick={() => {
                                    handleSendMessage();
                                }}
                            >
                                <MdSend />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
        </div>
    );
};

export default ParticipantChat;
